import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTimeEntriesByMonth, useUpdateTimeEntry } from '../../../state/data/interaction';
import {
  formatMinutesLabel, entryTimestampMs, resolveAuthorId, MIN_MINUTES_LOG_TIME, MAX_MINUTES_LOG_TIME,
} from '../../../logic/timeTracking';
import LogTimeModal from './LogTimeModal';

function entryDateLabel(entry) {
  const ts = entryTimestampMs(entry);
  if (!ts) return '—';
  const d = new Date(ts);
  // Manual entries carry a user-picked day; auto entries a precise timestamp.
  return entry.manual
    ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : d.toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
    });
}

function authorLabel(entry, currentUser) {
  const authorId = entry.author?.id != null ? String(entry.author.id) : null;
  const myId = resolveAuthorId(currentUser) ?? null;
  if (authorId && myId && authorId === myId) return 'by you';
  if (entry.author?.firstname) return `by ${entry.author.firstname}`;
  return '';
}

function EntryRow({ entry, currentUser }) {
  const updateMut = useUpdateTimeEntry();
  const [editing, setEditing] = useState(false);
  const [minutes, setMinutes] = useState('');
  const [editError, setEditError] = useState(null);

  const startEdit = () => {
    setMinutes(String(Math.max(1, Math.round((Number(entry.time) || 0) / 60))));
    setEditError(null);
    setEditing(true);
  };

  const saveEdit = () => {
    const m = Number(minutes);
    if (!Number.isInteger(m) || m < MIN_MINUTES_LOG_TIME || m > MAX_MINUTES_LOG_TIME) {
      setEditError(`1–${MAX_MINUTES_LOG_TIME} min`);
      return;
    }
    updateMut.mutate(
      { entryId: entry.id, minutes: m },
      { onSuccess: () => setEditing(false), onError: (e) => setEditError(String(e?.message || e)) },
    );
  };

  return (
    <div className="time-entry-row">
      <span className="time-entry-row__kind">
        {entry.manual ? 'Manual' : 'Auto'}
      </span>
      {editing ? (
        <span className="time-entry-row__edit">
          <input
            className="rx-input"
            type="number"
            min={MIN_MINUTES_LOG_TIME}
            max={MAX_MINUTES_LOG_TIME}
            step={1}
            value={minutes}
            aria-label="Entry duration in minutes"
            onChange={(e) => setMinutes(e.target.value)}
            style={{ width: 72, padding: '4px 8px' }}
          />
          <button type="button" className="btn-primary" style={{ fontSize: 12, padding: '4px 10px' }} onClick={saveEdit} disabled={updateMut.isPending}>
            {updateMut.isPending ? 'Saving…' : 'Save'}
          </button>
          <button type="button" className="btn-secondary" style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => setEditing(false)} disabled={updateMut.isPending}>
            Cancel
          </button>
          {editError && <span className="detail1" style={{ color: 'var(--wi-status-bad)' }}>{editError}</span>}
        </span>
      ) : (
        <span className="time-entry-row__duration">{formatMinutesLabel(Number(entry.time) || 0)}</span>
      )}
      <span className="detail1 text-secondary time-entry-row__meta">
        {entryDateLabel(entry)}{authorLabel(entry, currentUser) ? ` · ${authorLabel(entry, currentUser)}` : ''}
      </span>
      {!editing && (
        <button
          type="button"
          className="time-entry-row__editbtn"
          aria-label="Edit entry duration"
          title="Edit duration"
          onClick={startEdit}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" /></svg>
        </button>
      )}
    </div>
  );
}

// Full time-log modal (opened from the timer chip): month-by-month interaction
// time + manual add. Totals summed from entries; no CPT codes surfaced.
export default function TimeLogModal({ patient, carePeriod, currentUser, onClose }) {
  const groups = useTimeEntriesByMonth(patient?.userid);
  const [logTimeOpen, setLogTimeOpen] = useState(false);

  useEffect(() => {
    // Capture-phase + stopImmediatePropagation: the slide-over's own window
    // Escape handler navigates the whole drawer away — Escape here must close
    // ONLY this modal. The nested Log time form owns Escape while open.
    const onKey = (e) => {
      if (e.key !== 'Escape' || logTimeOpen) return;
      e.stopImmediatePropagation();
      onClose();
    };
    window.addEventListener('keydown', onKey, true);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey, true);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, logTimeOpen]);

  // Portaled to body: rendered inside the slide-over panel, the panel's
  // transform makes position:fixed resolve against the PANEL → cropped overlay.
  return createPortal((
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content time-log-modal" onClick={(e) => e.stopPropagation()}>
        <header className="modal-header">
          <h2 className="header3">Time log</h2>
          <button className="modal-close" aria-label="Close" onClick={onClose}>×</button>
        </header>
        <div className="modal-body time-log-modal__body">
          <div className="time-tab__header">
            <p className="detail1 text-secondary" style={{ margin: 0 }}>
              Interaction time tracked automatically while this patient&apos;s file is open, plus manual entries.
            </p>
            <button type="button" className="btn-primary" style={{ fontSize: 13, padding: '8px 16px', flexShrink: 0 }} onClick={() => setLogTimeOpen(true)}>
              Log time
            </button>
          </div>

          {groups.isLoading && (
            <div style={{ marginTop: 16 }}>
              {[1, 2, 3].map((i) => (
                <div key={i} className="skeleton-bar" style={{ width: '100%', height: 40, marginBottom: 8, borderRadius: 8 }} />
              ))}
            </div>
          )}
          {groups.error && (
            <div className="error-banner">Could not load the time log. {String(groups.error.message || groups.error)}</div>
          )}
          {!groups.isLoading && !groups.error && (groups.data?.length ?? 0) === 0 && (
            <div className="body2 text-secondary" style={{ padding: '24px 0' }}>
              No time recorded yet. The timer starts automatically when this file is open; use “Log time” for time spent elsewhere (calls, chart review in Elation…).
            </div>
          )}

          {(groups.data ?? []).map((group) => (
            <section key={group.monthKey} className="time-month">
              <div className="time-month__header">
                <span className="header4">{group.label}</span>
                <span className="body2 text-secondary">{formatMinutesLabel(group.totalSeconds)} total</span>
              </div>
              <div className="time-month__list">
                {group.entries.map((entry) => (
                  <EntryRow key={String(entry.id)} entry={entry} currentUser={currentUser} />
                ))}
              </div>
            </section>
          ))}
        </div>

        {logTimeOpen && (
          <LogTimeModal
            patient={patient}
            carePeriod={carePeriod}
            currentUser={currentUser}
            onClose={() => setLogTimeOpen(false)}
          />
        )}
      </div>
    </div>
  ), document.body);
}
